import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Alert,
  Chip,
  Switch,
  Paper,
  TablePagination,
} from "@mui/material";
import { DataGrid, useGridApiContext } from "@mui/x-data-grid";
import { Send, Clock, ArrowLeft, Search, Check } from "react-feather";
import axios from "../../utils/axios";
import { config } from "../../constants";
import { InlineEditCell } from "./InlineEditCell";

const STEPS = ["Content", "Recipients", "Review & Send"];

const AutoFilterMenu = React.forwardRef(function AutoFilterMenu(props, ref) {
  const apiRef = useGridApiContext();
  React.useEffect(() => {
    apiRef.current.hideColumnMenu();
    const panelEl = document.querySelector(".MuiDataGrid-panel");
    if (panelEl) {
      apiRef.current.hideFilterPanel();
    } else {
      apiRef.current.showFilterPanel(props.colDef?.field);
    }
  }, []);
  return null;
});

const dataGridSx = {
  fontSize: "0.75rem",
  border: "none",
  "& .MuiDataGrid-columnHeaderTitle": { fontSize: "0.75rem", fontWeight: 600 },
  "& .MuiDataGrid-cell": { fontSize: "0.75rem", py: 0.25, display: "flex", alignItems: "center" },
  "& .MuiDataGrid-row": { cursor: "pointer" },
  "& .MuiDataGrid-columnHeaders": { backgroundColor: "#fff" },
  "& .MuiDataGrid-sortIcon": { display: "none" },
  "& .MuiDataGrid-columnSeparator": { visibility: "hidden" },
  "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator": { visibility: "visible" },
  "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
  "& .MuiTablePagination-toolbar": { alignItems: "center", minHeight: 40 },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.75rem", margin: 0 },
  "& .MuiTablePagination-select": { fontSize: "0.75rem" },
  "& .MuiDataGrid-selectedRowCount": { display: "none" },
};

const CampaignWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0 });
  const pollRef = useRef(null);

  // Clean up polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Step 1: Content
  const [campaignName, setCampaignName] = useState(() => {
    const d = new Date();
    const month = d.toLocaleString("en-US", { month: "long" });
    const day = d.getDate();
    const suffix = [,"st","nd","rd"][day % 10 > 3 ? 0 : (day % 100 - day % 10 !== 10) * (day % 10)] || "th";
    return `${month} ${day}${suffix}`;
  });
  const [smsBody, setSmsBody] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(false);

  // Step 2: Recipients
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // Inline editing
  const [editingContact, setEditingContact] = useState(null); // { contactId, field }
  const editingContactRef = useRef(null);
  const updateEditingContact = useCallback((val) => {
    editingContactRef.current = val;
    setEditingContact(val);
  }, []);

  const handleSaveContactField = async (contactId, field, value) => {
    updateEditingContact(null);
    const apiField = field === "name" ? "contact_name" : field;
    try {
      await axios.put(`${config.url.SMS_URL}/api/sms/contacts/${contactId}`, { [apiField]: value });
      setContacts((prev) => prev.map((c) => c.id === contactId ? { ...c, [field]: value } : c));
    } catch (err) {
      console.error("Error saving contact field:", err);
      toast.error("Failed to save contact");
    }
  };

  // Step 3: Schedule
  const [campaignType, setCampaignType] = useState("one_off");
  const [scheduleDateTime, setScheduleDateTime] = useState("");
  const customerTimezone = localStorage.getItem("timezone") || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Load contacts
  useEffect(() => {
    const loadContacts = async () => {
      setContactsLoading(true);
      try {
        const res = await axios.get(`${config.url.SMS_URL}/api/sms/contacts`);
        const primary = (res.data.contacts || []).filter((c) => c.is_primary_contact && !c.is_opted_out && c.phone);
        const seen = new Set();
        const data = primary.filter((c) => {
          const phone = c.phone.replace(/\D/g, '');
          if (seen.has(phone)) return false;
          seen.add(phone);
          return true;
        });
        setContacts(data);
        setSelectedContactIds(new Set(data.map((c) => c.id)));
      } catch (err) {
        console.error("Error loading contacts:", err);
      } finally {
        setContactsLoading(false);
      }
    };
    loadContacts();
  }, []);

  // Load campaign data when editing
  useEffect(() => {
    if (!editId) return;
    const loadCampaign = async () => {
      try {
        const res = await axios.get(`${config.url.SMS_URL}/api/sms/campaigns`);
        const campaign = (res.data.campaigns || []).find((c) => c.id === Number(editId));
        if (campaign) {
          setCampaignName(campaign.name || "");
          setSmsEnabled(campaign.sms_enabled !== false);
          setEmailEnabled(campaign.email_enabled === true);
          if (campaign.campaign_type) setCampaignType(campaign.campaign_type);
          if (campaign.schedule_datetime) setScheduleDateTime(campaign.schedule_datetime);
        }
      } catch (err) {
        console.error("Error loading campaign:", err);
        setError("Failed to load campaign data");
      }
    };
    loadCampaign();
  }, [editId]);


  const handleSend = async () => {
    if (!campaignName.trim()) {
      toast.info("Please provide a campaign name.");
      return;
    }
    if (selectedContactIds.size === 0) {
      toast.info("Please select at least one recipient.");
      return;
    }
    if (smsEnabled && !smsBody.trim() && !emailEnabled) {
      toast.info("Please enter a message.");
      return;
    }

    setSending(true);
    setError(null);

    try {
      // 1. Create/update campaign
      const payload = {
        name: campaignName,
        campaign_type: campaignType,
        schedule_datetime: campaignType === "one_off_scheduled" && scheduleDateTime ? scheduleDateTime : null,
        schedule_timezone: campaignType === "one_off_scheduled" ? customerTimezone : null,
        sms_enabled: smsEnabled,
        email_enabled: emailEnabled,
        is_active: campaignType === "one_off_scheduled",
      };

      let campaignId = editId;
      if (editId) {
        await axios.put(`${config.url.SMS_URL}/api/sms/campaigns/${editId}`, payload);
      } else {
        const res = await axios.post(`${config.url.SMS_URL}/api/sms/campaigns`, payload);
        campaignId = res.data.id;
      }

      // 2. Build messages array from selected contacts
      const selectedContacts = contacts.filter((c) => selectedContactIds.has(c.id));
      const messages = [];
      for (const contact of selectedContacts) {
        if (smsEnabled && contact.phone) {
          messages.push({
            customer_name: contact.company || contact.name,
            person_name: contact.name,
            phone: contact.phone,
            email: contact.email || "",
            message: smsBody,
            channel: "sms",
          });
        }
        if (emailEnabled && contact.email) {
          messages.push({
            customer_name: contact.company || contact.name,
            person_name: contact.name,
            phone: contact.phone || "",
            email: contact.email,
            message: emailBody,
            subject: emailSubject,
            channel: "email",
          });
        }
      }

      if (messages.length === 0) {
        toast.info("No valid recipients for the selected channels.");
        setSending(false);
        return;
      }

      setSendProgress({ sent: 0, total: messages.length });

      // 3. Send now or schedule for later
      if (campaignType === "one_off_scheduled") {
        // Store messages for the scheduler — do NOT send now
        await axios.post(
          `${config.url.SMS_URL}/api/sms/campaigns/${campaignId}/schedule`,
          { messages }
        );
        const dt = new Date(scheduleDateTime);
        const formatted = dt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
        toast.success(`Campaign scheduled for ${formatted}`);
        setSending(false);
        navigate("/crm/campaigns");
      } else {
        const runId = crypto.randomUUID();
        const total = messages.length;

        await axios.post(
          `${config.url.SMS_URL}/api/sms/campaigns/${campaignId}/send?type=campaign`,
          { messages, campaign_run_id: runId }
        );

        // Poll for real-time progress
        setSendProgress({ sent: 0, total });

        pollRef.current = setInterval(async () => {
          try {
            const prog = await axios.get(
              `${config.url.SMS_URL}/api/sms/campaigns/${campaignId}/send-progress?run_id=${runId}&total=${total}`
            );
            setSendProgress({ sent: prog.data.sent, total });
            if (prog.data.done) {
              clearInterval(pollRef.current);
              pollRef.current = null;
              const smsCount = messages.filter((m) => m.channel === "sms").length;
              const emailCount = messages.filter((m) => m.channel === "email").length;
              const parts = [];
              if (smsCount > 0) parts.push(`${smsCount} SMS`);
              if (emailCount > 0) parts.push(`${emailCount} email${emailCount !== 1 ? "s" : ""}`);
              toast.success(`Sent ${parts.join(", ")}!`);
              setSending(false);
              navigate("/crm/campaigns");
            }
          } catch (e) {
            // Ignore polling errors — will retry on next interval
          }
        }, 2000);
      }
    } catch (err) {
      console.error("Send error:", err);
      setError(err.response?.data?.error || err.error || "Failed to send campaign");
      setSending(false);
    }
  };


  const contactColumns = [
    {
      field: "customer_name",
      headerName: "Customer",
      flex: 1,
      minWidth: 240,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "name",
      headerName: "Contact",
      flex: 1,
      minWidth: 80,
      renderCell: (params) => {
        const editing = editingContactRef.current;
        if (editing?.contactId === params.row.id && editing?.field === "name") {
          return (
            <InlineEditCell
              initialValue={params.value || ""}
              onSave={(v) => handleSaveContactField(params.row.id, "name", v)}
              onCancel={() => updateEditingContact(null)}
              placeholder="Contact name"
            />
          );
        }
        return (
          <Box onClick={(e) => { e.stopPropagation(); updateEditingContact({ contactId: params.row.id, field: "name" }); }} sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", cursor: "pointer" }}>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", "&:hover": { textDecoration: "underline" } }}>
              {params.value || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "phone",
      headerName: "Phone",
      width: 140,
      renderCell: (params) => {
        const editing = editingContactRef.current;
        if (editing?.contactId === params.row.id && editing?.field === "phone") {
          return (
            <InlineEditCell
              initialValue={params.value || ""}
              onSave={(v) => handleSaveContactField(params.row.id, "phone", v)}
              onCancel={() => updateEditingContact(null)}
              placeholder="+1234567890"
              validate={(v) => v === "" || /^[\d\-+() ]*$/.test(v)}
            />
          );
        }
        return (
          <Box onClick={(e) => { e.stopPropagation(); updateEditingContact({ contactId: params.row.id, field: "phone" }); }} sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", cursor: "pointer" }}>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", "&:hover": { textDecoration: "underline" } }}>
              {params.value || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 180,
      renderCell: (params) => {
        const editing = editingContactRef.current;
        if (editing?.contactId === params.row.id && editing?.field === "email") {
          return (
            <InlineEditCell
              initialValue={params.value || ""}
              onSave={(v) => handleSaveContactField(params.row.id, "email", v)}
              onCancel={() => updateEditingContact(null)}
              placeholder="email@example.com"
              validate={(v) => v === "" || /^[^\s]*$/.test(v)}
            />
          );
        }
        return (
          <Box onClick={(e) => { e.stopPropagation(); updateEditingContact({ contactId: params.row.id, field: "email" }); }} sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", cursor: "pointer" }}>
            <Typography variant="body2" sx={{ fontSize: "0.75rem", "&:hover": { textDecoration: "underline" } }}>
              {params.value || "-"}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const renderStep = () => {
    switch (activeStep) {
      case 0: // Content
        return (
          <Box sx={{ maxWidth: 800 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Campaign Content
            </Typography>

            {/* Channel toggles */}
            <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
              <FormControlLabel
                control={<Switch checked={smsEnabled} onChange={(e) => setSmsEnabled(e.target.checked)} />}
                label="SMS"
              />
            </Box>

            {!smsEnabled && !emailEnabled && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Enable at least one channel.
              </Alert>
            )}

            {smsEnabled && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  SMS Message
                </Typography>
                <TextField
                  multiline
                  minRows={10}
                  maxRows={20}
                  placeholder="Type your SMS message..."
                  value={smsBody}
                  onChange={(e) => setSmsBody(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Box>
            )}

            {emailEnabled && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Email
                </Typography>
                <TextField
                  label="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  multiline
                  minRows={6}
                  maxRows={12}
                  placeholder="Type your email body..."
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  fullWidth
                  size="small"
                />
              </Box>
            )}
          </Box>
        );

      case 1: // Recipients
        return (
          <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">Select Recipients</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedContactIds.size} of {contacts.length} contacts selected
              </Typography>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              {contactsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <DataGrid
                  autoHeight
                  rows={contacts}
                  columns={contactColumns}
                  density="compact"
                  checkboxSelection
                  keepNonExistentRowsSelected
                  rowSelectionModel={{ type: "include", ids: selectedContactIds }}
                  onRowSelectionModelChange={(newModel) => {
                    if (newModel.type === "exclude") {
                      const excluded = newModel.ids || new Set();
                      setSelectedContactIds(new Set(contacts.map((c) => c.id).filter((id) => !excluded.has(id))));
                    } else {
                      setSelectedContactIds(newModel.ids || new Set());
                    }
                  }}
                  disableRowSelectionOnClick
                  disableColumnSorting
                  slots={{ columnMenuIcon: () => <Search size={12} />, columnMenu: AutoFilterMenu, footer: () => null }}
                  slotProps={{ loadingOverlay: { variant: "skeleton", noRowsVariant: "skeleton" } }}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[10, 25, 50, 100]}
                  sx={dataGridSx}
                />
              )}
            </Box>
          </Box>
        );

      case 2: // Review & Send
        const selectedContacts = contacts.filter((c) => selectedContactIds.has(c.id));
        const smsRecipients = smsEnabled ? selectedContacts.filter((c) => c.phone) : [];
        const emailRecipients = emailEnabled ? selectedContacts.filter((c) => c.email) : [];

        return (
          <Box sx={{ maxWidth: 800 }}>
            {/* Summary */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Campaign Summary
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{campaignName || "(no name)"}</Typography>
                </Box>
                {smsEnabled && (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">SMS Recipients</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{smsRecipients.length}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>SMS Preview</Typography>
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Box sx={{
                          backgroundColor: "#34C759",
                          color: "#fff",
                          borderRadius: "18px 18px 4px 18px",
                          px: 1.75,
                          py: 1,
                          maxWidth: "80%",
                          whiteSpace: "pre-wrap",
                          fontSize: "0.85rem",
                          lineHeight: 1.4,
                          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        }}>
                          {smsBody || "(empty)"}
                        </Box>
                      </Box>
                    </Box>
                  </>
                )}
                {emailEnabled && (
                  <>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">Email Recipients</Typography>
                      <Chip label={emailRecipients.length} size="small" />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">Email Subject</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{emailSubject || "(no subject)"}</Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Paper>

            {/* Schedule */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                When to Send
              </Typography>
              <RadioGroup value={campaignType} onChange={(e) => setCampaignType(e.target.value)}>
                <FormControlLabel value="one_off" control={<Radio size="small" />} label="Send Now" />
                <FormControlLabel value="one_off_scheduled" control={<Radio size="small" />} label="Schedule for Later" />
              </RadioGroup>
              {campaignType === "one_off_scheduled" && (
                <Box sx={{ mt: 1.5, pl: 3.5 }}>
                  <TextField
                    type="datetime-local"
                    value={scheduleDateTime}
                    onChange={(e) => setScheduleDateTime(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Timezone: {customerTimezone}
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Send button */}
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSend}
                disabled={sending || (!smsEnabled && !emailEnabled) || !campaignName.trim()}
                startIcon={sending ? <CircularProgress size={18} sx={{ color: "white" }} /> : campaignType === "one_off_scheduled" ? <Clock size={18} /> : <Send size={18} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  px: 4,
                }}
              >
                {sending
                  ? campaignType === "one_off_scheduled"
                    ? "Scheduling..."
                    : sendProgress.total > 0
                    ? `Sending (${sendProgress.sent}/${sendProgress.total})...`
                    : "Sending..."
                  : campaignType === "one_off_scheduled"
                  ? "Schedule Campaign"
                  : "Send Now"}
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  const stepLabel = (idx) => {
    if (idx === 1) return `Recipients (${selectedContactIds.size})`;
    return STEPS[idx];
  };

  return (
    <>
      <Helmet title="Datajolt | One Off Blast" />
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        {error && (
          <Alert severity="error" sx={{ mx: 1, mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Main layout: sidebar + content */}
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>
          {/* Left Sidebar */}
          <Box
            sx={{
              width: 220,
              minWidth: 220,
              borderRight: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              px: 2,
              py: 2,
            }}
          >
            {/* Campaign Name Input */}
            <TextField
              label="Campaign Name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              fullWidth
              size="small"
              required
              sx={{ mb: 3 }}
            />

            {/* Vertical Stepper */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {STEPS.map((label, idx) => {
                const isCompleted = idx < activeStep;
                const isActive = idx === activeStep;
                const isFuture = idx > activeStep;

                return (
                  <Box key={label}>
                    {/* Step row */}
                    <Box
                      onClick={() => {
                        if (isCompleted || isActive) setActiveStep(idx);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        cursor: isFuture ? "default" : "pointer",
                        py: 0.5,
                        "&:hover": isFuture ? {} : { opacity: 0.8 },
                      }}
                    >
                      {/* Step circle */}
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          minWidth: 28,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          color: isCompleted ? "#fff" : isActive ? "#fff" : "text.disabled",
                          bgcolor: isCompleted ? "success.main" : isActive ? "primary.main" : "grey.200",
                          transition: "all 0.2s",
                        }}
                      >
                        {isCompleted ? <Check size={14} /> : idx + 1}
                      </Box>
                      {/* Step label */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive ? 600 : 400,
                          color: isFuture ? "text.disabled" : "text.primary",
                          fontSize: "0.8rem",
                        }}
                      >
                        {stepLabel(idx)}
                      </Typography>
                    </Box>

                    {/* Connector line */}
                    {idx < STEPS.length - 1 && (
                      <Box
                        sx={{
                          width: 2,
                          height: 24,
                          bgcolor: idx < activeStep ? "success.main" : "grey.200",
                          ml: "13px",
                          transition: "background-color 0.2s",
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Right Content Area */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                m: 2,
                p: 3,
                overflow: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Step content */}
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                {renderStep()}
              </Box>

              {/* Navigation buttons */}
              {activeStep < 2 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pt: 2, mt: 2, borderTop: 1, borderColor: "divider" }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep((prev) => prev - 1)}
                    sx={{ textTransform: "none" }}
                  >
                    Back
                  </Button>
                  {activeStep === 1 && contacts.length > 0 && (
                    <TablePagination
                      component="div"
                      count={contacts.length}
                      page={paginationModel.page}
                      onPageChange={(e, newPage) => setPaginationModel((prev) => ({ ...prev, page: newPage }))}
                      rowsPerPage={paginationModel.pageSize}
                      onRowsPerPageChange={(e) => setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      sx={{ border: 0, "& .MuiTablePagination-toolbar": { minHeight: 36, pl: 0, pr: 0 }, "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.75rem", m: 0 }, "& .MuiTablePagination-select": { fontSize: "0.75rem" } }}
                    />
                  )}
                  <Button
                    variant="contained"
                    onClick={() => setActiveStep((prev) => prev + 1)}
                    disabled={activeStep === 0 && (!smsEnabled && !emailEnabled || (smsEnabled && !smsBody.trim()) || (emailEnabled && !emailBody.trim()))}
                    sx={{ textTransform: "none" }}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CampaignWizard;
